import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import API from '../../API/Main/API';
import { ErrorProperties } from '../../API/identityAPIProperties';
import {
	ApprovePendingSoapNotesProperties,
	CreateSoapNoteProperties,
	GetDSUSoapNotesProperties,
	GetExcelReportProperties,
	GetPendingSoapNotesProperties,
	GetSoapNoteProperties,
	GetSoapNotesProperties,
	InitialSoapNoteStateProperties,
	SoapNotesDataProperties,
	UnlockSoapNoteProperties,
	UpdateAssessmentProperties,
	UpdateBehaviorTreatmentModificationProperties,
	UpdateBehaviorTreatmentProperties,
	UpdateBillingCredentialedProperties,
	UpdateDirectSupervisionProperties,
	UpdateDiscretionarySessionProperties,
	UpdateMeetAndGreetProperties,
	UpdateParentTrainingProperties,
	UpdateTreatmentPlanningProperties,
} from '../../API/ClientAPIHelpers/soapNoteProperties';
import { resetState } from '../../resetState';

export const initialSoapNoteState: InitialSoapNoteStateProperties = {
	loading: false,
	isUpdating: false,
	initialLoading: false,
	bcbaStatuses: [],
	btStatuses: [],
	behaviorTreatmentOptions: {
		barrierToTreatmentOptions: [],
		behaviorTreatmentTechniqueOptions: [],
		promptLevelOptions: [],
		reinforcementOptions: [],
		behaviorTreatmentFuturePlanOptions: [],
		clientMoodOptions: [],
		clientResponseToTreatmentOptions: [],
		levelOfMaladaptiveBehaviorOptions: [],
		maladaptiveBehaviorTechniqueOptions: [],
		skillDomainAddressedOptions: [],
		behaviorTreatmentParticipantOptions: [],
	},
	directSupervisionOptions: {
		feedbackOptions: [],
		directSupervisionParticipantOptions: [],
	},
	assessmentOptions: {
		assessmentToolOptions: [],
		assessmentParticipantOptions: [],
		treatmentPlanOptions: [],
		assessmentActivityOptions: [],
	},
	behaviorTreatmentModificationOptions: {
		barrierToTreatmentOptions: [],
		behaviorTreatmentModificationInterventionOptions: [],
		reinforcementOptions: [],
		responseToInterventionOptions: [],
		behaviorTreatmentModificationParticipantOptions: [],
	},
	parentTrainingOptions: {
		directParentTrainingOptions: [],
		parentTrainingParticipantOptions: [],
	},
	treatmentPlanningOptions: {
		treatmentPlanningParticipantOptions: [],
		treatmentUpdateOptions: [],
	},
	meetAndGreetOptions: {
		meetAndGreetBarrierToTreatmentOptions: [],
		meetAndGreetParticipantOptions: [],
	},
	soapNotes: {
		query: null,
		page: 1,
		totalNumberOfItems: 0,
		numberOfItems: 0,
		totalPages: 1,
		hasNextPage: false,
		hasPreviousPage: false,
	},
	dsuSoapNotes: {
		query: null,
		page: 1,
		totalNumberOfItems: 0,
		numberOfItems: 0,
		totalPages: 1,
		hasNextPage: false,
		hasPreviousPage: false,
	},
	pendingSoapNotes: {
		query: null,
		page: 1,
		totalNumberOfItems: 0,
		numberOfItems: 0,
		totalPages: 1,
		hasNextPage: false,
		hasPreviousPage: false,
	},
	soapNote: null,
	locations: null,
	telehealtLocations: [],
	error: {
		status: 0,
		title: '',
	},
	soapNoteData: {
		supervisor: {
			authoritationType: {
				id: 0,
				name: '',
			},
			fullName: '',
			id: '',
			role: '',
		},
	},
	soapNotesDrafts: { query: null },
};

export const updateBillingCredentialed = createAsyncThunk(
	'soapNote/update/user/notes',
	async (data: UpdateBillingCredentialedProperties, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.updateBillingCredentialed(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const getTelehealthLocations = createAsyncThunk('soapNote/telehealthlocations', async (_, thunkAPI) => {
	const response = await API.ClientAPI.TelehealthLocation.getTelehealthLocations();

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response;
});

export const getSoapNotesUser = createAsyncThunk(
	'soapNote/get/notes',
	async (data: GetSoapNotesProperties, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.getSoapNotesUser(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as SoapNotesDataProperties;
	},
);

export const getSoapNotesUserDrafts = createAsyncThunk(
	'soapNote/get/notes/pending',
	async ({ pageSize, userId }: { pageSize: string; userId: string }, thunkAPI) => {
		const endDate = dayjs().format('MM-DD-YYYY');
		const startDate = dayjs().startOf('month').format('MM-DD-YYYY');
		const data: GetSoapNotesProperties = {
			page: '1',
			pageSize,
			userId,
			status: '0',
			startDate,
			endDate,
		};
		const response = await API.ClientAPI.SoapNote.getSoapNotesUser(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as SoapNotesDataProperties;
	},
);

export const getPendingDSUSoapNotes = createAsyncThunk(
	'soapNote/get/notes/pending-dsu',
	async (data: GetExcelReportProperties, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.getPendingDSUSoapNotes(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as {
			file: Blob;
			fileName: string;
		};
	},
);

export const getExpiredSessions = createAsyncThunk(
	'soapNote/get/notes/expired-sessions',
	async (data: GetExcelReportProperties, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.getExpiredSessions(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as {
			file: Blob;
			fileName: string;
		};
	},
);

export const getSoapNotesAdmin = createAsyncThunk(
	'soapNote/get/notes/admin',
	async (data: GetSoapNotesProperties, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.getSoapNotesAdmin(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as SoapNotesDataProperties;
	},
);

export const getSoapNote = createAsyncThunk('soapNote/get', async (data: GetSoapNoteProperties, thunkAPI) => {
	const response = await API.ClientAPI.SoapNote.getSoapNote(data);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response;
});

export const unlockSoapNote = createAsyncThunk('soapNote/unlock', async (data: UnlockSoapNoteProperties, thunkAPI) => {
	const response = await API.ClientAPI.SoapNote.unlockSoapNote(data);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response;
});

export const createSoapNote = createAsyncThunk('soapNote/create', async (data: CreateSoapNoteProperties, thunkAPI) => {
	const response = await API.ClientAPI.SoapNote.createSoapNote(data);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return { reportId: response.id } as { reportId: string };
});

export const getSoapNotePDF = createAsyncThunk('soapNote/get/pdf', async (data: GetSoapNoteProperties, thunkAPI) => {
	const response = await API.ClientAPI.SoapNote.getSoapNotePDF(data);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response;
});

export const getBehaviorTreatmentOptions = createAsyncThunk(
	'soapNote/behaviorTreatment/options',
	async ({ clientId, sessionId }: { clientId: string; sessionId: string }, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.getBehaviorTreatmentOptions(clientId, sessionId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const updateBehaviorTreatment = createAsyncThunk(
	'soapNote/behaviorTreatment/update',
	async (data: UpdateBehaviorTreatmentProperties, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.updateBehaviorTreatment(data, thunkAPI.signal);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const getBehaviorTreatmentModificationOptions = createAsyncThunk(
	'soapNote/behaviorTreatmentModification/options',
	async ({ clientId, sessionId }: { clientId: string; sessionId: string }, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.getBehaviorTreatmentModificationOptions(clientId, sessionId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const updateBehaviorTreatmentModification = createAsyncThunk(
	'soapNote/behaviorTreatmentModification/update',
	async (data: UpdateBehaviorTreatmentModificationProperties, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.updateBehaviorTreatmentModification(data, thunkAPI.signal);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const getAssessmentOptions = createAsyncThunk(
	'soapNote/assessment/options',
	async ({ clientId, sessionId }: { clientId: string; sessionId: string }, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.getAssessmentOptions(clientId, sessionId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const updateAssessment = createAsyncThunk(
	'soapNote/assessment/update',
	async (data: UpdateAssessmentProperties, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.updateAssessment(data, thunkAPI.signal);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const getDirectSupervisionOptions = createAsyncThunk(
	'soapNote/directSupervision/options',
	async ({ clientId, sessionId }: { clientId: string; sessionId: string }, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.getDirectSupervisionOptions(clientId, sessionId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const updateDirectSupervision = createAsyncThunk(
	'soapNote/directSupervision/update',
	async (data: UpdateDirectSupervisionProperties, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.updateDirectSupervision(data, thunkAPI.signal);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const getParentTrainingOptions = createAsyncThunk(
	'soapNote/parentTraining/options',
	async ({ clientId, sessionId }: { clientId: string; sessionId: string }, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.getParentTrainingOptions(clientId, sessionId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const updateParentTraining = createAsyncThunk(
	'soapNote/parentTraining/update',
	async (data: UpdateParentTrainingProperties, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.updateParentTraining(data, thunkAPI.signal);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const getTreatmentPlanningOptions = createAsyncThunk(
	'soapNote/TreatmentPlanning/options',
	async ({ clientId, sessionId }: { clientId: string; sessionId: string }, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.getTreatmentPlanningOptions(clientId, sessionId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const updateTreatmentPlanning = createAsyncThunk(
	'soapNote/TreatmentPlanning/update',
	async (data: UpdateTreatmentPlanningProperties, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.updateTreatmentPlanning(data, thunkAPI.signal);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const getMeetAndGreetOptions = createAsyncThunk(
	'soapNote/MeetAndGreet/options',
	async ({ clientId, sessionId }: { clientId: string; sessionId: string }, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.getMeetAndGreetOptions(clientId, sessionId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const updateMeetAndGreet = createAsyncThunk(
	'soapNote/MeetAndGreet/update',
	async (data: UpdateMeetAndGreetProperties, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.updateMeetAndGreet(data, thunkAPI.signal);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const updateDiscretionarySession = createAsyncThunk(
	'soapNote/discretionarySession/update',
	async (data: UpdateDiscretionarySessionProperties, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.updateDiscretionarySession(data, thunkAPI.signal);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const getPendingSoapNotes = createAsyncThunk(
	'soapNote/pending-approval',
	async (data: GetPendingSoapNotesProperties, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.getPendingNotes(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const approveSoapNotes = createAsyncThunk(
	'soapNote/approve/all',
	async (data: ApprovePendingSoapNotesProperties, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.approvePendingNotes(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const getDSUSoapNotes = createAsyncThunk(
	'soapNote/dsu/get',
	async (data: GetDSUSoapNotesProperties, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.getDSUNotes(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const downloadDSUSoapNotes = createAsyncThunk(
	'soapNote/dsu/get/download',
	async (data: GetDSUSoapNotesProperties, thunkAPI) => {
		const response = await API.ClientAPI.SoapNote.downloadDSUNotes(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as {
			file: Blob;
			fileName: string;
		};
	},
);

const soapNoteSlice = createSlice({
	name: 'soapNote',
	initialState: initialSoapNoteState,
	reducers: {
		clearErrors(state) {
			state.error = initialSoapNoteState.error;
		},
		clearDrafts(state) {
			state.soapNotesDrafts = initialSoapNoteState.soapNotesDrafts;
		},
		setBcbaStatuses(state, action) {
			state.bcbaStatuses = action.payload;
		},
		setBtStatuses(state, action) {
			state.btStatuses = action.payload;
		},
	},
	extraReducers: (builder) => {
		// createSoapNote
		builder.addCase(createSoapNote.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(createSoapNote.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(createSoapNote.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// unlock
		builder.addCase(unlockSoapNote.pending, (state) => {
			state.isUpdating = true;
		});
		builder.addCase(unlockSoapNote.fulfilled, (state) => {
			state.isUpdating = false;
		});
		builder.addCase(unlockSoapNote.rejected, (state, action) => {
			state.isUpdating = false;
			state.error = action.payload as ErrorProperties;
		});
		// getSoapNotesUserDrafts
		builder.addCase(getSoapNotesUserDrafts.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getSoapNotesUserDrafts.fulfilled, (state, action) => {
			state.loading = false;
			state.soapNotesDrafts = action.payload;
		});
		builder.addCase(getSoapNotesUserDrafts.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getDSUSoapNotes
		builder.addCase(getDSUSoapNotes.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getDSUSoapNotes.fulfilled, (state, action) => {
			state.loading = false;
			state.dsuSoapNotes = action.payload;
		});
		builder.addCase(getDSUSoapNotes.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// downloadDSUSoapNotes
		builder.addCase(downloadDSUSoapNotes.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(downloadDSUSoapNotes.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(downloadDSUSoapNotes.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getPendingSoapNotes
		builder.addCase(getPendingSoapNotes.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getPendingSoapNotes.fulfilled, (state, action) => {
			state.loading = false;
			state.pendingSoapNotes = action.payload;
		});
		builder.addCase(getPendingSoapNotes.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// approveSoapNotes
		builder.addCase(approveSoapNotes.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(approveSoapNotes.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(approveSoapNotes.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getSoapNote
		builder.addCase(getSoapNote.pending, (state) => {
			state.initialLoading = true;
		});
		builder.addCase(getSoapNote.fulfilled, (state, action) => {
			state.initialLoading = false;
			state.soapNote = action.payload;
		});
		builder.addCase(getSoapNote.rejected, (state, action) => {
			state.initialLoading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getSoapNotesUser
		builder.addCase(getSoapNotesUser.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getSoapNotesUser.fulfilled, (state, action) => {
			state.loading = false;
			state.soapNotes = action.payload;
		});
		builder.addCase(getSoapNotesUser.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getTelehealthLocations
		builder.addCase(getTelehealthLocations.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getTelehealthLocations.fulfilled, (state, action) => {
			state.loading = false;
			state.telehealtLocations = action.payload;
		});
		builder.addCase(getTelehealthLocations.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getSoapNotesAdmin
		builder.addCase(getSoapNotesAdmin.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getSoapNotesAdmin.fulfilled, (state, action) => {
			state.loading = false;
			state.soapNotes = action.payload;
		});
		builder.addCase(getSoapNotesAdmin.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getBehaviorTreatmentOptions
		builder.addCase(getBehaviorTreatmentOptions.pending, (state) => {
			state.initialLoading = true;
		});
		builder.addCase(getBehaviorTreatmentOptions.fulfilled, (state, action) => {
			state.initialLoading = false;
			state.behaviorTreatmentOptions = action.payload.options;
			state.soapNoteData = action.payload.data;
			state.locations = action.payload.locations;
		});
		builder.addCase(getBehaviorTreatmentOptions.rejected, (state, action) => {
			state.initialLoading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getBehaviorTreatmentModificationOptions
		builder.addCase(getBehaviorTreatmentModificationOptions.pending, (state) => {
			state.initialLoading = true;
		});
		builder.addCase(getBehaviorTreatmentModificationOptions.fulfilled, (state, action) => {
			state.initialLoading = false;
			state.behaviorTreatmentModificationOptions = action.payload.options;
			state.locations = action.payload.locations;
			state.soapNoteData = action.payload.data;
		});
		builder.addCase(getBehaviorTreatmentModificationOptions.rejected, (state, action) => {
			state.initialLoading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getDirectSupervisionOptions
		builder.addCase(getDirectSupervisionOptions.pending, (state) => {
			state.initialLoading = true;
		});
		builder.addCase(getDirectSupervisionOptions.fulfilled, (state, action) => {
			state.initialLoading = false;
			state.directSupervisionOptions = action.payload.options;
			state.locations = action.payload.locations;
			state.soapNoteData = action.payload.data;
		});
		builder.addCase(getDirectSupervisionOptions.rejected, (state, action) => {
			state.initialLoading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getAssessmentOptions
		builder.addCase(getAssessmentOptions.pending, (state) => {
			state.initialLoading = true;
		});
		builder.addCase(getAssessmentOptions.fulfilled, (state, action) => {
			state.initialLoading = false;
			state.assessmentOptions = action.payload.options;
			state.locations = action.payload.locations;
			state.soapNoteData = action.payload.data;
		});
		builder.addCase(getAssessmentOptions.rejected, (state, action) => {
			state.initialLoading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getParentTrainingOptions
		builder.addCase(getParentTrainingOptions.pending, (state) => {
			state.initialLoading = true;
		});
		builder.addCase(getParentTrainingOptions.fulfilled, (state, action) => {
			state.initialLoading = false;
			state.soapNoteData = action.payload.data;
			state.locations = action.payload.locations;
			state.parentTrainingOptions = action.payload.options;
		});
		builder.addCase(getParentTrainingOptions.rejected, (state, action) => {
			state.initialLoading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getTreatmentPlanningOptions
		builder.addCase(getTreatmentPlanningOptions.pending, (state) => {
			state.initialLoading = true;
		});
		builder.addCase(getTreatmentPlanningOptions.fulfilled, (state, action) => {
			state.initialLoading = false;
			state.soapNoteData = action.payload.data;
			state.locations = action.payload.locations;
			state.treatmentPlanningOptions = action.payload.options;
		});
		builder.addCase(getTreatmentPlanningOptions.rejected, (state, action) => {
			state.initialLoading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getMeetAndGreetOptions
		builder.addCase(getMeetAndGreetOptions.pending, (state) => {
			state.initialLoading = true;
		});
		builder.addCase(getMeetAndGreetOptions.fulfilled, (state, action) => {
			state.initialLoading = false;
			state.soapNoteData = action.payload.data;
			state.locations = action.payload.locations;
			state.meetAndGreetOptions = action.payload.options;
		});
		builder.addCase(getMeetAndGreetOptions.rejected, (state, action) => {
			state.initialLoading = false;
			state.error = action.payload as ErrorProperties;
		});
		// updateAssessment
		builder.addCase(updateAssessment.pending, (state) => {
			state.isUpdating = true;
			state.error = initialSoapNoteState.error;
		});
		builder.addCase(updateAssessment.fulfilled, (state) => {
			state.isUpdating = false;
		});
		builder.addCase(updateAssessment.rejected, (state, action) => {
			state.isUpdating = false;
			state.error = action.payload as ErrorProperties;
		});
		// updateBehaviorTreatmentModification
		builder.addCase(updateBehaviorTreatmentModification.pending, (state) => {
			state.isUpdating = true;
			state.error = initialSoapNoteState.error;
		});
		builder.addCase(updateBehaviorTreatmentModification.fulfilled, (state) => {
			state.isUpdating = false;
		});
		builder.addCase(updateBehaviorTreatmentModification.rejected, (state, action) => {
			state.isUpdating = false;
			state.error = action.payload as ErrorProperties;
		});
		// updateBehaviorTreatment
		builder.addCase(updateBehaviorTreatment.pending, (state) => {
			state.isUpdating = true;
			state.error = initialSoapNoteState.error;
		});
		builder.addCase(updateBehaviorTreatment.fulfilled, (state) => {
			state.isUpdating = false;
		});
		builder.addCase(updateBehaviorTreatment.rejected, (state, action) => {
			state.isUpdating = false;
			state.error = action.payload as ErrorProperties;
		});
		// updateDirectSupervision
		builder.addCase(updateDirectSupervision.pending, (state) => {
			state.isUpdating = true;
			state.error = initialSoapNoteState.error;
		});
		builder.addCase(updateDirectSupervision.fulfilled, (state) => {
			state.isUpdating = false;
		});
		builder.addCase(updateDirectSupervision.rejected, (state, action) => {
			state.isUpdating = false;
			state.error = action.payload as ErrorProperties;
		});
		// updateParentTraining
		builder.addCase(updateParentTraining.pending, (state) => {
			state.isUpdating = true;
			state.error = initialSoapNoteState.error;
		});
		builder.addCase(updateParentTraining.fulfilled, (state) => {
			state.isUpdating = false;
		});
		builder.addCase(updateParentTraining.rejected, (state, action) => {
			state.isUpdating = false;
			state.error = action.payload as ErrorProperties;
		});
		// updateTreatmentPlanning
		builder.addCase(updateTreatmentPlanning.pending, (state) => {
			state.isUpdating = true;
			state.error = initialSoapNoteState.error;
		});
		builder.addCase(updateTreatmentPlanning.fulfilled, (state) => {
			state.isUpdating = false;
		});
		builder.addCase(updateTreatmentPlanning.rejected, (state, action) => {
			state.isUpdating = false;
			state.error = action.payload as ErrorProperties;
		});
		// updateMeetAndGreet
		builder.addCase(updateMeetAndGreet.pending, (state) => {
			state.isUpdating = true;
			state.error = initialSoapNoteState.error;
		});
		builder.addCase(updateMeetAndGreet.fulfilled, (state) => {
			state.isUpdating = false;
		});
		builder.addCase(updateMeetAndGreet.rejected, (state, action) => {
			state.isUpdating = false;
			state.error = action.payload as ErrorProperties;
		});
		// updateDiscretionarySession
		builder.addCase(updateDiscretionarySession.pending, (state) => {
			state.isUpdating = true;
			state.error = initialSoapNoteState.error;
		});
		builder.addCase(updateDiscretionarySession.fulfilled, (state) => {
			state.isUpdating = false;
		});
		builder.addCase(updateDiscretionarySession.rejected, (state, action) => {
			state.isUpdating = false;
			state.error = action.payload as ErrorProperties;
		});
		// reset state
		builder.addCase(resetState, () => initialSoapNoteState);
	},
});

export const { clearErrors, clearDrafts, setBcbaStatuses, setBtStatuses } = soapNoteSlice.actions;
export default soapNoteSlice.reducer;
